import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import {
	Button,
	Input,
	Label,
	Modal,
	ModalBody,
	ModalFooter,
	ModalHeader,
	Spinner,
} from "reactstrap";
import { ChevronDown, Search } from "lucide-react";
import {
	adaptKeywordPromptsPayload,
	buildKeywordTopicGroups,
	intentLabel,
	journeyLabel,
} from "../monitoringAdapters";
import { getKeywordPrompts } from "../../../../services/monitoringApi";

const INTENT_FILTERS = [
	{ value: "all", label: "All intents" },
	{ value: "informational", label: "Informational" },
	{ value: "commercial", label: "Commercial" },
	{ value: "transactional", label: "Transactional" },
	{ value: "navigational", label: "Navigational" },
	{ value: "local", label: "Local" },
];

const JOURNEY_FILTERS = [
	{ value: "all", label: "All journey stages" },
	{ value: "awareness", label: "Awareness" },
	{ value: "consideration", label: "Consideration" },
	{ value: "decision", label: "Decision" },
];

function formatVolume(n) {
	const v = Number(n) || 0;
	if (v >= 1000000) return `${(v / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
	if (v >= 1000) {
		return v.toLocaleString("en-US");
	}
	return String(v);
}

function IntentChip({ intent }) {
	if (!intent) return null;
	const key = String(intent).toLowerCase();
	return (
		<span className={`monitoring__intent-chip monitoring__intent-chip--${key}`}>
			{intentLabel(key) || key.charAt(0).toUpperCase() + key.slice(1)}
		</span>
	);
}

function JourneyChip({ stage }) {
	if (!stage) return null;
	const key = String(stage).toLowerCase().replace(/\s+/g, "_");
	return (
		<span className={`monitoring__journey-chip monitoring__journey-chip--${key}`}>
			{journeyLabel(key) || key.charAt(0).toUpperCase() + key.slice(1)}
		</span>
	);
}

function CitedStatusPill({ cited }) {
	const isCited = cited === true;
	return (
		<span
			className={`monitoring__cited-pill monitoring__cited-pill--${
				isCited ? "yes" : "no"
			}`}
		>
			{isCited ? "Cited" : "Not cited"}
		</span>
	);
}

function NestedPromptsTable({ prompts = [], loading, error }) {
	if (loading) {
		return (
			<div className="monitoring__kw-nested">
				<p className="text-muted small mb-0 py-2">Loading prompts…</p>
			</div>
		);
	}
	if (error) {
		return (
			<div className="monitoring__kw-nested">
				<p className="monitoring__empty--error small mb-0 py-2">{error}</p>
			</div>
		);
	}
	if (!prompts.length) {
		return (
			<div className="monitoring__kw-nested">
				<p className="text-muted small mb-0 py-2">
					No tracked prompts mapped to this keyword yet.
				</p>
			</div>
		);
	}
	return (
		<div className="monitoring__kw-nested">
			<table className="monitoring__table monitoring__kw-nested-table">
				<thead>
					<tr>
						<th scope="col">Prompt</th>
						<th scope="col">Tags</th>
						<th scope="col">Latest run</th>
					</tr>
				</thead>
				<tbody>
					{prompts.map((p) => (
						<tr key={p.promptId}>
							<td className="monitoring__kw-nested-prompt">{p.promptText}</td>
							<td>
								{p.isBranded ? (
									<span className="monitoring__branded-chip">Branded</span>
								) : (
									<span className="text-muted">—</span>
								)}
							</td>
							<td>
								<CitedStatusPill cited={p.cited} />
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

function hasTrackedPrompts(keyword) {
	return Number(keyword?.mappedPrompts) > 0;
}

function KeywordRow({
	keyword,
	selected,
	expanded,
	onToggleSelect,
	onToggleExpand,
	promptsCache,
	websiteId,
	onPromptsLoaded,
}) {
	const cited = keyword.citedOnMapped;
	const citedLabel =
		cited && cited.of != null
			? `cited ${cited.cited}/${cited.of}`
			: keyword.mappedPrompts > 0
				? `cited —/${keyword.mappedPrompts}`
				: "cited 0/0";
	const cache = promptsCache?.[keyword.keywordId];
	const cacheRef = React.useRef(promptsCache);
	cacheRef.current = promptsCache;

	useEffect(() => {
		if (!expanded || !websiteId || keyword.keywordId == null) return undefined;
		const existing = cacheRef.current?.[keyword.keywordId];
		if (existing?.loaded) return undefined;

		let cancelled = false;
		onPromptsLoaded?.(keyword.keywordId, {
			loading: true,
			loaded: false,
			error: null,
		});

		(async () => {
			const res = await getKeywordPrompts(websiteId, keyword.keywordId);
			if (cancelled) return;
			if (!res.ok) {
				onPromptsLoaded?.(keyword.keywordId, {
					loading: false,
					loaded: true,
					error: res.message || "Failed to load prompts",
					prompts: [],
				});
				return;
			}
			const adapted = adaptKeywordPromptsPayload({
				keyword_id: res.keywordId,
				keyword: res.keyword,
				prompts: res.prompts,
				total: res.total,
			});
			onPromptsLoaded?.(keyword.keywordId, {
				loading: false,
				loaded: true,
				error: null,
				prompts: adapted.prompts,
			});
		})();

		return () => {
			cancelled = true;
		};
	}, [expanded, websiteId, keyword.keywordId, onPromptsLoaded]);

	return (
		<li
			className={`monitoring__kw-row${selected ? " is-selected" : ""}${
				expanded ? " is-expanded" : ""
			}${!hasTrackedPrompts(keyword) ? " is-unmapped" : ""}`}
		>
			<div className="monitoring__kw-row-main">
				<label className="monitoring__kw-row-check">
					<input
						type="checkbox"
						checked={selected}
						onChange={() => onToggleSelect(keyword.keywordId)}
						onClick={(e) => e.stopPropagation()}
					/>
				</label>
				<button
					type="button"
					className="monitoring__kw-row-body"
					onClick={() => onToggleExpand(keyword.keywordId)}
					aria-expanded={expanded}
				>
					<span className="monitoring__kw-row-left">
						<span className="monitoring__kw-text">{keyword.keyword}</span>
						<IntentChip intent={keyword.intent} />
						<JourneyChip stage={keyword.journeyStage} />
						<span className="monitoring__kw-stats text-muted">
							{formatVolume(keyword.searchVolume)}/mo · KD{" "}
							{keyword.keywordDifficulty != null
								? Math.round(keyword.keywordDifficulty)
								: "—"}
						</span>
					</span>
					<span className="monitoring__kw-row-right">
						<span className="monitoring__kw-mapped text-muted">
							{keyword.mappedPrompts} prompt
							{keyword.mappedPrompts === 1 ? "" : "s"} · {citedLabel}
						</span>
						<ChevronDown
							size={16}
							className={`monitoring__kw-chevron${expanded ? " is-open" : ""}`}
							aria-hidden
						/>
					</span>
				</button>
			</div>
			{expanded && (
				<NestedPromptsTable
					prompts={cache?.prompts || []}
					loading={Boolean(cache?.loading)}
					error={cache?.error}
				/>
			)}
		</li>
	);
}

function TopicGroup({
	group,
	isUnassigned = false,
	isOpen = true,
	onToggleOpen,
	allTopics,
	selectedIds,
	expandedIds,
	showUnmapped,
	forcedShowHidden,
	onToggleForcedShow,
	onToggleSelect,
	onToggleExpand,
	promptsCache,
	websiteId,
	onPromptsLoaded,
	renameTopicId,
	renameValue,
	onRenameStart,
	onRenameChange,
	onRenameApply,
	onRenameCancel,
	mergeTopicId,
	mergeTargetId,
	onMergeStart,
	onMergeTarget,
	onMergeApply,
	onMergeCancel,
	onDeleteTopic,
	splitTopicId,
	splitName,
	onSplitStart,
	onSplitNameChange,
	onSplitApply,
	onSplitCancel,
	actionBusy,
}) {
	const topicId = group.topicId;
	const isRenaming = !isUnassigned && renameTopicId === topicId;
	const isMerging = !isUnassigned && mergeTopicId === topicId;
	const isSplitting = !isUnassigned && splitTopicId === topicId;
	const isEditing = isRenaming || isMerging || isSplitting;

	const mapped = (group.keywords || []).filter(hasTrackedPrompts);
	const unmapped = (group.keywords || []).filter((k) => !hasTrackedPrompts(k));
	const showHidden = showUnmapped || forcedShowHidden;
	const visible = showHidden ? group.keywords || [] : mapped;
	const hiddenCount = showUnmapped ? 0 : unmapped.length;
	const selectedInGroup = (group.keywords || []).filter((k) =>
		selectedIds.has(k.keywordId)
	).length;

	// Hide topics that only have unmapped keywords until the global checkbox (or per-topic reveal) is on.
	if (!showHidden && mapped.length === 0) {
		return null;
	}

	return (
		<article
			className={`monitoring__topic-card monitoring__kw-topic-card${
				isOpen ? " monitoring__topic-card--open" : ""
			}${isEditing ? " monitoring__topic-card--editing" : ""}`}
		>
			<header className="monitoring__topic-header monitoring__kw-topic-header">
				{isRenaming ? (
					<div className="monitoring__topic-header-left">
						<div className="monitoring__topic-inline-edit">
							<input
								className="monitoring__topic-rename-input"
								value={renameValue}
								onChange={(e) => onRenameChange(e.target.value)}
								aria-label="Topic name"
								autoFocus
							/>
							<Button
								color="primary"
								size="sm"
								disabled={actionBusy === `rename-${topicId}`}
								onClick={() => onRenameApply(topicId)}
							>
								{actionBusy === `rename-${topicId}` ? (
									<Spinner size="sm" />
								) : (
									"Apply"
								)}
							</Button>
							<Button
								color="secondary"
								outline
								size="sm"
								onClick={onRenameCancel}
							>
								Cancel
							</Button>
						</div>
					</div>
				) : (
					<button
						type="button"
						className="monitoring__topic-accordion-btn"
						aria-expanded={isOpen}
						onClick={onToggleOpen}
					>
						<ChevronDown
							size={16}
							className={`monitoring__topic-chevron${
								isOpen ? " is-open" : ""
							}`}
							aria-hidden
						/>
						<div className="monitoring__topic-header-left">
							<h3 className="monitoring__topic-name">{group.name}</h3>
							{group.intentPrimary && <IntentChip intent={group.intentPrimary} />}
							<span className="monitoring__topic-meta text-muted">
								{group.keywordCount} keyword
								{group.keywordCount === 1 ? "" : "s"} · {group.promptCount} prompt
								{group.promptCount === 1 ? "" : "s"}
							</span>
						</div>
					</button>
				)}

				<div className="monitoring__topic-header-right">
					{isMerging ? (
						<div className="monitoring__topic-inline-edit">
							<span className="small text-muted">Merge into...</span>
							<MoveToTopicDropdown
								topics={allTopics}
								value={mergeTargetId}
								excludeTopicId={topicId}
								onChange={onMergeTarget}
								placeholder="Merge into..."
							/>
							<Button
								color="primary"
								size="sm"
								disabled={
									mergeTargetId == null || actionBusy === `merge-${topicId}`
								}
								onClick={() => onMergeApply(topicId)}
							>
								{actionBusy === `merge-${topicId}` ? (
									<Spinner size="sm" />
								) : (
									"Apply"
								)}
							</Button>
							<Button
								color="secondary"
								outline
								size="sm"
								onClick={onMergeCancel}
							>
								Cancel
							</Button>
						</div>
					) : isSplitting ? (
						<div className="monitoring__topic-inline-edit">
							<span className="small text-muted">New topic name</span>
							<input
								className="monitoring__topic-rename-input"
								value={splitName}
								onChange={(e) => onSplitNameChange(e.target.value)}
								placeholder="New topic name"
								aria-label="New topic name"
								autoFocus
								onKeyDown={(e) => {
									if (e.key === "Enter") {
										e.preventDefault();
										onSplitApply?.(topicId);
									}
								}}
							/>
							<Button
								color="primary"
								size="sm"
								disabled={
									!String(splitName || "").trim() ||
									actionBusy === `split-${topicId}`
								}
								onClick={() => onSplitApply?.(topicId)}
							>
								{actionBusy === `split-${topicId}` ? (
									<Spinner size="sm" />
								) : (
									"Apply"
								)}
							</Button>
							<Button
								color="secondary"
								outline
								size="sm"
								onClick={onSplitCancel}
							>
								Cancel
							</Button>
						</div>
					) : (
						<>
							<span className="monitoring__kw-topic-stat">
								Volume {formatVolume(group.volumeTotal || 0)}
							</span>
							<span className="monitoring__topic-cited">
								Cited {group.citedPrompts}/{group.testedPrompts || 0}
							</span>
							{!isUnassigned && (
								<>
									<button
										type="button"
										className="monitoring__topic-action"
										onClick={() => onRenameStart(topicId, group.name)}
									>
										Rename
									</button>
									<button
										type="button"
										className="monitoring__topic-action"
										onClick={() => onMergeStart(topicId)}
									>
										Merge
									</button>
									{selectedInGroup > 0 && (
										<button
											type="button"
											className="monitoring__topic-action"
											onClick={() => onSplitStart?.(topicId)}
										>
											Split selection
										</button>
									)}
									<button
										type="button"
										className="monitoring__topic-action monitoring__topic-action--danger"
										disabled={actionBusy === `delete-${topicId}`}
										onClick={() => onDeleteTopic(topicId, group.name)}
									>
										Delete
									</button>
								</>
							)}
						</>
					)}
				</div>
			</header>

			{isOpen ? (
				<>
					{(group.keywords || []).length === 0 ? (
						<p className="monitoring__empty text-muted mb-0 px-3 py-3">
							No keywords in this topic yet.
						</p>
					) : (
						<ul className="monitoring__kw-list list-unstyled mb-0">
							{visible.map((k) => (
								<KeywordRow
									key={k.keywordId ?? k.keyword}
									keyword={k}
									selected={selectedIds.has(k.keywordId)}
									expanded={expandedIds.has(k.keywordId)}
									onToggleSelect={onToggleSelect}
									onToggleExpand={onToggleExpand}
									promptsCache={promptsCache}
									websiteId={websiteId}
									onPromptsLoaded={onPromptsLoaded}
								/>
							))}
						</ul>
					)}

					{hiddenCount > 0 && (
						<div className="monitoring__kw-hidden-row">
							<span>
								+ {hiddenCount} more keyword{hiddenCount === 1 ? "" : "s"} in this
								topic with no tracked prompts yet —{" "}
								<button
									type="button"
									className="monitoring__link-btn"
									onClick={onToggleForcedShow}
								>
									{forcedShowHidden ? "hide them" : "show them"}
								</button>
							</span>
						</div>
					)}
				</>
			) : null}
		</article>
	);
}

export function MoveToTopicDropdown({
	topics = [],
	value,
	onChange,
	excludeTopicId = null,
	placeholder = "Move to topic...",
	disabled = false,
}) {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const rootRef = useRef(null);

	const options = useMemo(() => {
		const q = query.trim().toLowerCase();
		return topics
			.filter((t) => t.topicId !== excludeTopicId)
			.filter((t) => !q || t.name.toLowerCase().includes(q))
			.sort((a, b) => a.name.localeCompare(b.name));
	}, [topics, excludeTopicId, query]);

	const selected = topics.find((t) => t.topicId === value);

	useEffect(() => {
		if (!open) return undefined;
		function onDoc(e) {
			if (!rootRef.current?.contains(e.target)) setOpen(false);
		}
		document.addEventListener("mousedown", onDoc);
		return () => document.removeEventListener("mousedown", onDoc);
	}, [open]);

	return (
		<div
			className={`monitoring__topic-picker${open ? " is-open" : ""}`}
			ref={rootRef}
		>
			<button
				type="button"
				className="monitoring__topic-picker-trigger"
				disabled={disabled}
				aria-expanded={open}
				onClick={() => setOpen((v) => !v)}
			>
				<span>{selected?.name || placeholder}</span>
				<i className="fas fa-chevron-down" aria-hidden />
			</button>
			{open && (
				<div className="monitoring__topic-picker-menu">
					<input
						type="search"
						className="monitoring__topic-picker-search"
						placeholder="Search topics…"
						value={query}
						autoFocus
						onChange={(e) => setQuery(e.target.value)}
					/>
					<div className="monitoring__topic-picker-list">
						{options.length === 0 ? (
							<p className="text-muted small mb-0 px-2 py-2">No topics found</p>
						) : (
							options.map((t) => (
								<button
									key={t.topicId}
									type="button"
									className={`monitoring__topic-picker-item${
										t.topicId === value
											? " monitoring__topic-picker-item--active"
											: ""
									}`}
									onClick={() => {
										onChange?.(t.topicId);
										setOpen(false);
										setQuery("");
									}}
								>
									{t.name}
								</button>
							))
						)}
					</div>
				</div>
			)}
		</div>
	);
}

/** Opaque custom select (avoids native <select> transparency bugs). */
export function MonitoringSelect({
	options = [],
	value,
	onChange,
	disabled = false,
	placeholder = "Select…",
	className = "",
	dropUp = false,
	"aria-label": ariaLabel,
}) {
	const [open, setOpen] = useState(false);
	const rootRef = useRef(null);

	const selected = useMemo(
		() => options.find((o) => String(o.value) === String(value ?? "")),
		[options, value]
	);

	useEffect(() => {
		if (!open) return undefined;
		function onDoc(e) {
			if (!rootRef.current?.contains(e.target)) setOpen(false);
		}
		function onKey(e) {
			if (e.key === "Escape") setOpen(false);
		}
		document.addEventListener("mousedown", onDoc);
		document.addEventListener("keydown", onKey);
		return () => {
			document.removeEventListener("mousedown", onDoc);
			document.removeEventListener("keydown", onKey);
		};
	}, [open]);

	return (
		<div
			className={`monitoring__dropdown${open ? " is-open" : ""}${
				className ? ` ${className}` : ""
			}`}
			ref={rootRef}
		>
			<button
				type="button"
				className="monitoring__dropdown-trigger"
				disabled={disabled}
				aria-expanded={open}
				aria-haspopup="listbox"
				aria-label={ariaLabel}
				onClick={() => setOpen((v) => !v)}
			>
				<span className="monitoring__dropdown-label">
					{selected?.label || placeholder}
				</span>
				<i className="fas fa-chevron-down" aria-hidden />
			</button>
			{open && (
				<ul
					className={`monitoring__dropdown-menu${
						dropUp ? " monitoring__dropdown-menu--dropup" : ""
					}`}
					role="listbox"
				>
					{options.map((o) => {
						const active = String(o.value) === String(value ?? "");
						return (
							<li key={String(o.value) || "__empty__"} role="presentation">
								<button
									type="button"
									role="option"
									aria-selected={active}
									className={`monitoring__dropdown-option${
										active ? " is-active" : ""
									}`}
									onClick={() => {
										onChange?.(o.value);
										setOpen(false);
									}}
								>
									{o.label}
								</button>
							</li>
						);
					})}
				</ul>
			)}
		</div>
	);
}

function parseImportKeywordLines(text) {
	const INTENT = new Set([
		"informational",
		"commercial",
		"navigational",
		"transactional",
		"local",
	]);
	const JOURNEY = new Set(["awareness", "consideration", "decision"]);

	const lines = String(text || "")
		.split(/\r?\n/)
		.map((s) => s.trim())
		.filter(Boolean);

	const rows = [];
	const seen = new Set();

	lines.forEach((line) => {
		const parts = line.split(",").map((p) => p.trim()).filter(Boolean);
		if (!parts.length) return;

		let keyword = parts[0];
		let intent = null;
		let journeyStage = null;

		if (parts.length >= 2) {
			const a = parts[1].toLowerCase();
			const b = parts[2] ? parts[2].toLowerCase() : "";
			if (INTENT.has(a)) intent = a;
			if (JOURNEY.has(b)) journeyStage = b;
			else if (JOURNEY.has(a) && !intent) journeyStage = a;
			if (!intent && !journeyStage && parts.length > 1) {
				keyword = line;
			}
		}

		const key = keyword.toLowerCase();
		if (!key || seen.has(key)) return;
		seen.add(key);

		if (intent || journeyStage) {
			rows.push({
				keyword,
				...(intent ? { intent } : {}),
				...(journeyStage ? { journey_stage: journeyStage } : {}),
			});
		} else {
			rows.push(keyword);
		}
	});

	return rows;
}

export function ImportKeywordsModal({
	isOpen,
	onClose,
	onSubmit,
	busy,
	lastImport,
}) {
	const [text, setText] = useState("");
	const [groupIntoTopics, setGroupIntoTopics] = useState(true);

	useEffect(() => {
		if (!isOpen) {
			setText("");
			setGroupIntoTopics(true);
		}
	}, [isOpen]);

	async function handleSubmit() {
		const rows = parseImportKeywordLines(text);
		if (!rows.length) return;
		const ok = await onSubmit?.(rows, { groupIntoTopics });
		if (ok) setText("");
	}

	return (
		<Modal
			isOpen={isOpen}
			toggle={onClose}
			centered
			className="monitoring__kw-import-modal"
		>
			<ModalHeader toggle={onClose}>Import keywords</ModalHeader>
			<ModalBody>
				<p className="monitoring__kw-import-lead text-muted">
					One keyword per line, or paste CSV with optional <code>intent</code>{" "}
					and <code>journey_stage</code> columns. Anything you omit is
					auto-detected.
				</p>

				<Label className="monitoring__field-label" for="kw-import-text">
					Keywords
				</Label>
				<Input
					id="kw-import-text"
					type="textarea"
					rows={7}
					className="monitoring__kw-import-textarea"
					value={text}
					onChange={(e) => setText(e.target.value)}
					placeholder={
						"coconut oil for senior dogs, informational, awareness\ndog itchy skin remedy\nbuy coconut oil for dogs, transactional, decision"
					}
					disabled={busy}
				/>

				<label className="monitoring__kw-import-check">
					<input
						type="checkbox"
						checked={groupIntoTopics}
						onChange={(e) => setGroupIntoTopics(e.target.checked)}
						disabled={busy}
					/>
					<span>
						<strong>Group into topics after import</strong> (recommended)
					</span>
				</label>
				<p className="monitoring__kw-import-hint text-muted">
					Runs the <strong>same clustering as keyword research</strong> — matches
					your existing topics first, creating a new one only when nothing fits.
					Without this, imported keywords land in <strong>Unassigned</strong>.
					You&apos;ll see the topic each keyword landed in when the import
					finishes.
				</p>

				{lastImport && (
					<div className="monitoring__kw-import-result" role="status">
						<p className="monitoring__kw-import-result-summary mb-0">
							Last import:{" "}
							<strong>
								{lastImport.added} keyword
								{lastImport.added === 1 ? "" : "s"} added
							</strong>
							{" · "}
							<strong>{lastImport.matched}</strong> matched existing topics
							{" · "}
							<strong>{lastImport.created}</strong> new topics created
							{lastImport.dupes > 0
								? ` · ${lastImport.dupes} duplicate${
										lastImport.dupes === 1 ? "" : "s"
									} skipped`
								: ""}
							.
						</p>
						{lastImport.topics?.length > 0 && (
							<p className="monitoring__kw-import-result-topics mb-0">
								{lastImport.topics
									.map((t) =>
										t.count != null ? `${t.name} (${t.count})` : t.name
									)
									.join(" · ")}
							</p>
						)}
					</div>
				)}
			</ModalBody>
			<ModalFooter>
				<Button color="secondary" outline onClick={onClose} disabled={busy}>
					Cancel
				</Button>
				<Button
					color="primary"
					onClick={handleSubmit}
					disabled={busy || !text.trim()}
				>
					{busy ? <Spinner size="sm" /> : "Import keywords"}
				</Button>
			</ModalFooter>
		</Modal>
	);
}

export default function KeywordsDashboard({
	websiteId,
	keywords = [],
	topicsPayload = null,
	total = 0,
	mappedCount = 0,
	candidateTotal = 0,
	busy = false,
	actionBusy = null,
	onReviewPending,
	onResearchMore,
	onImport,
	onBulkMove,
	onSplitSelection,
	onRenameTopic,
	onMergeTopic,
	onDeleteTopic,
	onAutoAssign,
	autoAssignBusy = false,
}) {
	const [showUnmapped, setShowUnmapped] = useState(false);
	const [intentFilter, setIntentFilter] = useState("all");
	const [journeyFilter, setJourneyFilter] = useState("all");
	const [searchInput, setSearchInput] = useState("");
	const [search, setSearch] = useState("");
	const [selectedIds, setSelectedIds] = useState(() => new Set());
	const [expandedIds, setExpandedIds] = useState(() => new Set());
	const [openTopicIds, setOpenTopicIds] = useState(() => new Set());
	const topicAccordionInitRef = useRef(false);
	const [promptsCache, setPromptsCache] = useState({});
	const [forcedShowByTopic, setForcedShowByTopic] = useState({});
	const [importOpen, setImportOpen] = useState(false);
	const [renameTopicId, setRenameTopicId] = useState(null);
	const [renameValue, setRenameValue] = useState("");
	const [mergeTopicId, setMergeTopicId] = useState(null);
	const [mergeTargetId, setMergeTargetId] = useState(null);
	const [splitTopicId, setSplitTopicId] = useState(null);
	const [splitName, setSplitName] = useState("");
	const [bulkTopicId, setBulkTopicId] = useState(null);
	const [deleteConfirm, setDeleteConfirm] = useState(null);
	const [lastImport, setLastImport] = useState(null);

	useEffect(() => {
		const t = window.setTimeout(() => setSearch(searchInput), 200);
		return () => window.clearTimeout(t);
	}, [searchInput]);

	const unmappedTotal = useMemo(
		() => (keywords || []).filter((k) => !hasTrackedPrompts(k)).length,
		[keywords]
	);

	const filteredKeywords = useMemo(() => {
		const q = search.trim().toLowerCase();
		return (keywords || []).filter((k) => {
			if (intentFilter !== "all" && k.intent !== intentFilter) return false;
			if (journeyFilter !== "all" && k.journeyStage !== journeyFilter) return false;
			if (q && !String(k.keyword || "").toLowerCase().includes(q)) return false;
			return true;
		});
	}, [keywords, intentFilter, journeyFilter, search]);

	// Keep unmapped in the group source so topics can show "+ N more" when the
	// global checkbox is off; TopicGroup decides what rows are visible.
	const groupSource = filteredKeywords;

	const grouped = useMemo(
		() => buildKeywordTopicGroups(groupSource, topicsPayload),
		[groupSource, topicsPayload]
	);

	// POC: with "show without tracked prompts" on → total topic volume desc;
	// with it off → mapped-keyword volume desc (adapter default). Ties → name A–Z.
	const orderedGroups = useMemo(() => {
		const groups = [...(grouped.groups || [])];
		if (!showUnmapped) return groups;
		return groups.sort((a, b) => {
			const byVol = (Number(b.volumeTotal) || 0) - (Number(a.volumeTotal) || 0);
			if (byVol !== 0) return byVol;
			return String(a.name || "").localeCompare(String(b.name || ""));
		});
	}, [grouped.groups, showUnmapped]);

	const allTopics = topicsPayload?.topics || [];

	useEffect(() => {
		if (topicAccordionInitRef.current) return;
		const hasTopics = orderedGroups.length > 0;
		const hasUnassigned = (grouped.unassigned?.keywords || []).length > 0;
		if (!hasTopics && !hasUnassigned) return;
		const next = new Set();
		if (hasTopics) next.add(String(orderedGroups[0].topicId));
		else if (hasUnassigned) next.add("unassigned");
		setOpenTopicIds(next);
		topicAccordionInitRef.current = true;
	}, [orderedGroups, grouped.unassigned]);

	function toggleTopicOpen(topicKey) {
		const key = String(topicKey);
		setOpenTopicIds((prev) => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	}	const selectedCount = selectedIds.size;
	const hasVisibleContent = useMemo(() => {
		const topicHasVisible = (group) => {
			const mapped = (group.keywords || []).filter(hasTrackedPrompts);
			return showUnmapped || mapped.length > 0;
		};
		const anyGroup = orderedGroups.some(topicHasVisible);
		const unassigned = grouped.unassigned?.keywords || [];
		const unassignedVisible =
			showUnmapped
				? unassigned.length > 0
				: unassigned.some(hasTrackedPrompts);
		return anyGroup || unassignedVisible;
	}, [orderedGroups, grouped.unassigned, showUnmapped]);

	const hasFilters =
		Boolean(search) || intentFilter !== "all" || journeyFilter !== "all";

	const clearSelection = useCallback(() => {
		setSelectedIds(new Set());
		setBulkTopicId(null);
	}, []);

	const toggleSelect = useCallback((id) => {
		if (id == null) return;
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}, []);

	const toggleExpand = useCallback((id) => {
		if (id == null) return;
		setExpandedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}, []);

	const onPromptsLoaded = useCallback((keywordId, patch) => {
		setPromptsCache((prev) => ({
			...prev,
			[keywordId]: { ...(prev[keywordId] || {}), ...patch },
		}));
	}, []);

	function clearInlineEdits() {
		setRenameTopicId(null);
		setRenameValue("");
		setMergeTopicId(null);
		setMergeTargetId(null);
		setSplitTopicId(null);
		setSplitName("");
	}

	async function handleImport(list, { groupIntoTopics }) {
		const result = await onImport?.(list, { groupIntoTopics });
		if (result?.ok) {
			setLastImport(result.summary || null);
			return true;
		}
		return false;
	}

	async function handleBulkMove() {
		if (bulkTopicId == null || !selectedIds.size) return;
		const ids = Array.from(selectedIds);
		const success = await onBulkMove?.(ids, bulkTopicId);
		if (success) clearSelection();
	}

	async function handleSplitApply(topicId) {
		if (topicId == null) return;
		const name = String(splitName || "").trim();
		if (!name) return;
		const ids = (keywords || [])
			.filter(
				(k) =>
					k.keywordId != null &&
					selectedIds.has(k.keywordId) &&
					Number(k.topicId) === Number(topicId)
			)
			.map((k) => k.keywordId);
		if (!ids.length) return;
		const success = await onSplitSelection?.(topicId, ids, name);
		if (success) {
			clearInlineEdits();
			clearSelection();
		}
	}

	async function confirmDeleteTopic() {
		if (!deleteConfirm?.id) return;
		const ok = await onDeleteTopic?.(deleteConfirm.id);
		if (ok !== false) setDeleteConfirm(null);
	}

	const topicProps = {
		allTopics,
		selectedIds,
		expandedIds,
		showUnmapped,
		onToggleSelect: toggleSelect,
		onToggleExpand: toggleExpand,
		promptsCache,
		websiteId,
		onPromptsLoaded,
		renameTopicId,
		renameValue,
		onRenameStart: (id, name) => {
			clearInlineEdits();
			setRenameTopicId(id);
			setRenameValue(name || "");
		},
		onRenameChange: setRenameValue,
		onRenameApply: async (id) => {
			const ok = await onRenameTopic?.(id, renameValue);
			if (ok) clearInlineEdits();
		},
		onRenameCancel: clearInlineEdits,
		mergeTopicId,
		mergeTargetId,
		onMergeStart: (id) => {
			clearInlineEdits();
			setMergeTopicId(id);
			setMergeTargetId(null);
		},
		onMergeTarget: setMergeTargetId,
		onMergeApply: async (id) => {
			const ok = await onMergeTopic?.(id, mergeTargetId);
			if (ok) clearInlineEdits();
		},
		onMergeCancel: clearInlineEdits,
		onDeleteTopic: (id, name) => {
			setDeleteConfirm({ id, name: name || "this topic" });
		},
		splitTopicId,
		splitName,
		onSplitStart: (id) => {
			clearInlineEdits();
			setSplitTopicId(id);
			setSplitName("");
		},
		onSplitNameChange: setSplitName,
		onSplitApply: handleSplitApply,
		onSplitCancel: clearInlineEdits,
		actionBusy,
	};

	return (
		<div className="monitoring__keywords-dashboard">
			<div className="monitoring__kw-summary">
				<div className="monitoring__kw-summary-left">
					<span className="monitoring__kw-summary-label">Keywords</span>
					<span className="monitoring__kw-summary-meta text-muted">
						{mappedCount} mapped to tracked prompts · {total} researched
					</span>
				</div>
				<label className="monitoring__kw-summary-toggle">
					<input
						type="checkbox"
						checked={showUnmapped}
						onChange={(e) => {
							const checked = e.target.checked;
							setShowUnmapped(checked);
							if (checked) setForcedShowByTopic({});
						}}
					/>
					<span>
						Show {unmappedTotal} without tracked prompts
					</span>
				</label>
			</div>

			<div className="monitoring__filter-bar monitoring__kw-filter-bar">
				<div className="monitoring__filter-bar-left">
					<select
						className="monitoring__filter-select monitoring__filter-pill"
						value={intentFilter}
						onChange={(e) => setIntentFilter(e.target.value)}
						aria-label="Filter by intent"
					>
						{INTENT_FILTERS.map((o) => (
							<option key={o.value} value={o.value}>
								{o.label}
							</option>
						))}
					</select>
					<select
						className="monitoring__filter-select monitoring__filter-pill"
						value={journeyFilter}
						onChange={(e) => setJourneyFilter(e.target.value)}
						aria-label="Filter by journey stage"
					>
						{JOURNEY_FILTERS.map((o) => (
							<option key={o.value} value={o.value}>
								{o.label}
							</option>
						))}
					</select>
					<div className="monitoring__filter-search-wrap">
						<Search size={14} aria-hidden />
						<input
							type="search"
							className="monitoring__filter-search"
							placeholder="Search keywords"
							value={searchInput}
							onChange={(e) => setSearchInput(e.target.value)}
							aria-label="Search keywords"
						/>
					</div>
				</div>
				<div className="monitoring__filter-bar-right">
					{candidateTotal > 0 && (
						<button
							type="button"
							className="monitoring__link-btn"
							onClick={onReviewPending}
						>
							Review {candidateTotal} pending →
						</button>
					)}
					<Button
						color="secondary"
						outline
						size="sm"
						className="monitoring__action-btn"
						disabled={busy}
						onClick={() => setImportOpen(true)}
					>
						Import keywords
					</Button>
					<Button
						color="primary"
						size="sm"
						className="monitoring__action-btn monitoring__action-btn--primary"
						disabled={busy}
						onClick={onResearchMore}
					>
						Research more →
					</Button>
				</div>
			</div>

			{selectedCount > 0 && (
				<div className="monitoring__bulk-bar monitoring__kw-bulk-bar">
					<div className="monitoring__bulk-bar-left">
						<strong>
							{selectedCount} selected
						</strong>
						<span className="text-muted">
							{" "}
							— move to a topic without leaving the list
						</span>
					</div>
					<div className="monitoring__bulk-bar-right">
						<MoveToTopicDropdown
							topics={allTopics}
							value={bulkTopicId}
							onChange={setBulkTopicId}
							disabled={busy}
						/>
						<Button
							color="primary"
							size="sm"
							disabled={bulkTopicId == null || busy}
							onClick={handleBulkMove}
						>
							{busy ? <Spinner size="sm" /> : "Apply"}
						</Button>
						<Button
							color="secondary"
							outline
							size="sm"
							disabled={busy}
							onClick={clearSelection}
						>
							Clear
						</Button>
					</div>
				</div>
			)}

			{!hasVisibleContent ? (
				<div className="monitoring__panel monitoring__empty-panel">
					{keywords.length === 0 ? (
						<>
							<p className="monitoring__empty text-muted mb-2">
								No keywords yet. Import a list or research more from your seed
								topics to start mapping keywords to tracked prompts.
							</p>
							<div className="d-flex gap-2 flex-wrap">
								<Button
									color="secondary"
									outline
									size="sm"
									onClick={() => setImportOpen(true)}
								>
									Import keywords
								</Button>
								<Button color="primary" size="sm" onClick={onResearchMore}>
									Research more →
								</Button>
							</div>
						</>
					) : (
						<p className="monitoring__empty text-muted mb-0">
							No keywords match your filters
							{hasFilters ? ". Try clearing filters." : "."}
						</p>
					)}
				</div>
			) : (
				<div className="monitoring__topic-stack monitoring__kw-topic-stack">
					{orderedGroups.map((group) => {
						const key = String(group.topicId);
						return (
							<TopicGroup
								key={key}
								group={group}
								isOpen={openTopicIds.has(key)}
								onToggleOpen={() => toggleTopicOpen(key)}
								forcedShowHidden={Boolean(forcedShowByTopic[key])}
								onToggleForcedShow={() =>
									setForcedShowByTopic((prev) => ({
										...prev,
										[key]: !prev[key],
									}))
								}
								{...topicProps}
							/>
						);
					})}

					{(grouped.unassigned?.keywords || []).length > 0 &&
						(showUnmapped ||
							(grouped.unassigned.keywords || []).some(hasTrackedPrompts) ||
							Boolean(forcedShowByTopic.unassigned)) && (
						<>
							<TopicGroup
								group={grouped.unassigned}
								isUnassigned
								isOpen={openTopicIds.has("unassigned")}
								onToggleOpen={() => toggleTopicOpen("unassigned")}
								forcedShowHidden={Boolean(forcedShowByTopic.unassigned)}
								onToggleForcedShow={() =>
									setForcedShowByTopic((prev) => ({
										...prev,
										unassigned: !prev.unassigned,
									}))
								}
								{...topicProps}
							/>
							<div className="monitoring__kw-unassigned-actions">
								<Button
									color="secondary"
									outline
									size="sm"
									disabled={autoAssignBusy}
									onClick={onAutoAssign}
								>
									{autoAssignBusy ? (
										<>
											<Spinner size="sm" className="me-1" /> Assigning…
										</>
									) : (
										"Auto-assign topics"
									)}
								</Button>
							</div>
						</>
					)}
				</div>
			)}

			<p className="monitoring__kw-footer text-muted">
				<span className="monitoring__kw-footer-strong">
					Topic → Keyword → Prompt
				</span>{" "}
				— expanding a keyword shows the tracked prompts generated from it, each
				flagged cited or not from the latest run. Keywords with no tracked
				prompts yet are hidden; use the checkbox above to include them.
			</p>

			<ImportKeywordsModal
				isOpen={importOpen}
				onClose={() => setImportOpen(false)}
				onSubmit={handleImport}
				busy={busy}
				lastImport={lastImport}
			/>

			<Modal
				isOpen={Boolean(deleteConfirm)}
				toggle={() => setDeleteConfirm(null)}
				centered
			>
				<ModalHeader toggle={() => setDeleteConfirm(null)}>
					Delete topic
				</ModalHeader>
				<ModalBody>
					Delete topic &ldquo;{deleteConfirm?.name}&rdquo;? Keywords in this
					topic may become unassigned.
				</ModalBody>
				<ModalFooter>
					<Button
						color="secondary"
						outline
						onClick={() => setDeleteConfirm(null)}
						disabled={actionBusy === `delete-${deleteConfirm?.id}`}
					>
						Cancel
					</Button>
					<Button
						color="danger"
						onClick={confirmDeleteTopic}
						disabled={actionBusy === `delete-${deleteConfirm?.id}`}
					>
						{actionBusy === `delete-${deleteConfirm?.id}` ? (
							<Spinner size="sm" />
						) : (
							"Delete topic"
						)}
					</Button>
				</ModalFooter>
			</Modal>
		</div>
	);
}
